import { Injectable } from '@angular/core';
import { EcommerceTrackingMapperService } from '@epicuro-next/addons/ecommerce-tracking-mappers';
import { List } from '@epicuro-next/models/engagement';
import { selectUseInternalIds } from '@epicuro-next/platform/configuration';
import { EcommerceActionsToTrack } from '@epicuro-next/platform/tracking';
import { selectActiveAccountCurrency } from '@epicuro-next/state/accounts-state';
import {
  selectDtoBasketFromEntities,
  selectOperableExtendedBasketItems,
} from '@epicuro-next/state/basket-state';
import {
  cachedProductResolved,
  loadPricesSuccess,
  loadProductSuccess,
  selectEcommerceTrackingSource,
  selectPricesForCurrentSelection,
  selectProductSource,
  selectSizeEntities,
  selectUIAvailableInventoriesForCurrentDeliveryPeriod,
} from '@epicuro-next/state/config-strip-state';
import { selectListEntities } from '@epicuro-next/state/list-entities';
import { selectLookbookEntityForTracking } from '@epicuro-next/state/lookbook-entities';
import { selectProductBasketSearchQuery } from '@epicuro-next/state/p-basket-state';
import {
  selectSiteName,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { gtmTrackActions } from '@epicuro-next/state/tracking';
import { selectUser } from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { EMPTY, forkJoin, of, race } from 'rxjs';
import { delay, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { defaultCollectorSelect } from '../shared/default-collector-select';
import { mapProductSource } from '../shared/map-product-source';

@Injectable()
export class DataCollectorsEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private ecommerceTrackingMapper: EcommerceTrackingMapperService,
  ) {}

  public pageTrackDataCollecting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((action) => action.name === EcommerceActionsToTrack.PageTrack),
      delay(500),
      concatLatestFrom(() => [
        this.store.select(selectUser),
        this.store.select(selectDtoBasketFromEntities),
        this.store.select(selectSiteName),
        this.store.select(selectSubSiteId),
        this.store.select(selectUseInternalIds),
        ...defaultCollectorSelect(this.store),
      ]),
      map(
        ([
          action,
          user,
          basket,
          site,
          subSite,
          useInternalIds,
          activeAccount,
          presetDisplayName,
        ]) =>
          gtmTrackActions.gtmSendTrackEvent({
            name: EcommerceActionsToTrack.PageTrack,
            payload: {
              ...action.payload,
              user,
              activeAccount,
              basket,
              site,
              subSite,
              useInternalIds,
              fullPath: `${window.location.pathname}${window.location.search}`,
              presetDisplayName,
            },
          }),
      ),
    ),
  );

  public selectVariant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.SelectVariant),
      mergeMap(({ eventMappingFunction, payload: { productId } }) =>
        forkJoin([
          of(eventMappingFunction),
          of(productId).pipe(take(1)),
          race(
            this.actions$.pipe(ofType(loadProductSuccess)),
            this.actions$.pipe(ofType(cachedProductResolved)),
          ).pipe(take(1)),
          this.actions$.pipe(ofType(loadPricesSuccess), take(1)),
        ]),
      ),
      concatLatestFrom(() => [
        this.store.select(selectPricesForCurrentSelection),
        this.store.select(selectActiveAccountCurrency),
        this.store.select(selectEcommerceTrackingSource),
        this.store.select(selectSizeEntities),
        this.store.select(selectDtoBasketFromEntities),
        this.store.select(selectProductSource),
        this.store.select(selectLookbookEntityForTracking),
        this.store.select(selectListEntities),
        this.store.select(
          selectUIAvailableInventoriesForCurrentDeliveryPeriod(false),
        ),
        ...defaultCollectorSelect(this.store),
      ]),
      switchMap(
        ([
          [eventMappingFunction, variantId, { product }],
          prices,
          currency,
          source,
          sizeEntities,
          basket,
          productSource,
          lookbookHashMap,
          listEntities,
          deliveryItems,
          activeAccount,
          presetDisplayName,
          ,
          user,
        ]) => {
          const gtmProductSource = mapProductSource(
            productSource,
            lookbookHashMap,
            listEntities,
          );

          let variant = product.children?.find(
            (children) => children.productId === variantId,
          );

          if (!variant) {
            const prepack = product.prepacks?.find(
              (item) => item.productId === variantId,
            );

            if (!prepack) {
              return EMPTY;
            }

            const size = sizeEntities[prepack.quantities[0].productId];
            if (!size?.variantReference) {
              return EMPTY;
            }

            variant = product.children?.find(
              (children) => children.productId === size.variantReference,
            );
          }

          if (!variant) {
            return EMPTY;
          }

          return this.ecommerceTrackingMapper
            .mapEventsFromAction(
              { variant: variant as Product, prices, source, currency },
              eventMappingFunction,
            )
            .pipe(
              switchMap((eventNames) =>
                eventNames.map((name) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name,
                    payload: {
                      variant: variant as Product,
                      prices,
                      source,
                      currency,
                      basket,
                      productSource,
                      mappedProductSource: gtmProductSource,
                      deliveryItems,
                      activeAccount,
                      presetDisplayName,
                      user,
                    },
                  }),
                ),
              ),
            );
        },
      ),
    ),
  );

  public activeAccountChangedDataCollector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.SetActiveAccount),
      switchMap(({ eventMappingFunction, payload }) =>
        this.ecommerceTrackingMapper
          .mapEventsFromAction(payload, eventMappingFunction)
          .pipe(
            switchMap((eventNames) =>
              eventNames.map((name) =>
                gtmTrackActions.gtmSendTrackEvent({
                  name,
                  payload,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public loadFiltersSuccessDataCollector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.LoadFiltersSuccess),
      concatLatestFrom(() => defaultCollectorSelect(this.store)),
      switchMap(
        ([
          { eventMappingFunction, payload },
          activeAccount,
          presetDisplayName,
          ,
          user,
        ]) =>
          this.ecommerceTrackingMapper
            .mapEventsFromAction(payload, eventMappingFunction)
            .pipe(
              switchMap((eventNames) =>
                eventNames.map((name) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name,
                    payload: { activeAccount, presetDisplayName, user },
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  public loginEventDataCollector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.Login),
      concatLatestFrom(() => [...defaultCollectorSelect(this.store)]),
      switchMap(
        ([
          { eventMappingFunction, payload },
          activeAccount,
          presetDisplayName,
          url,
        ]) =>
          this.ecommerceTrackingMapper
            .mapEventsFromAction({ ...payload, url }, eventMappingFunction)
            .pipe(
              switchMap((eventNames) =>
                eventNames.map((name) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name,
                    payload: { activeAccount, presetDisplayName },
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  public searchEventDataCollector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.Search),
      concatLatestFrom(() => [
        this.store.select(selectProductBasketSearchQuery),
        ...defaultCollectorSelect(this.store),
        this.store.select(selectOperableExtendedBasketItems),
      ]),
      switchMap(
        ([
          { eventMappingFunction, payload },
          searchTerm,
          activeAccount,
          presetDisplayName,
          url,
          user,
          items,
        ]) =>
          this.ecommerceTrackingMapper
            .mapEventsFromAction({ ...payload, url }, eventMappingFunction)
            .pipe(
              switchMap((eventNames) =>
                eventNames.map((name) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name,
                    payload: {
                      activeAccount,
                      presetDisplayName,
                      searchTerm,
                      user,
                      items,
                    },
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  public sendViewItemListOnListOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.OpenList),
      concatLatestFrom(() => defaultCollectorSelect(this.store)),
      switchMap(
        ([
          { eventMappingFunction, payload },
          activeAccount,
          presetDisplayName,
          ,
          user,
        ]) => {
          const list = payload.list as List;
          return this.ecommerceTrackingMapper
            .mapEventsFromAction(payload, eventMappingFunction)
            .pipe(
              switchMap((eventNames) =>
                eventNames.map((name) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name,
                    payload: {
                      listId: list.id,
                      listName: list.name,
                      items: [],
                      activeAccount,
                      presetDisplayName,
                      user,
                    },
                  }),
                ),
              ),
            );
        },
      ),
    ),
  );
}
